import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export interface StudentDetail {
  id: number | null;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  address?: string;
  photo_url?: string;
}

export interface CurriculumDetail {
  id: number;
  type: "course" | "track";
  title: string;
  slug: string;
  image_url?: string;
  online_price: number;
  onsite_price: number;
  payment_type?: string;
  instructor_name?: string;
}

export interface AdminEnrollmentItem {
  id: number;
  date: string;
  delivery_mode: "online" | "onsite";
  student_details: StudentDetail;
  curriculum: CurriculumDetail;
  is_enrolled: boolean;
  is_paid: boolean;
  is_completed: boolean;
  payment_amount?: number | null;
  payment_date?: string | null;
  payment_method?: string | null;
  last_active_date?: string | null;
  progress_percent: number;
  completed_lessons?: string[];
  total_lessons_count: number;
}

export interface AdminEnrollmentKpis {
  total_enrollments: number;
  online_count: number;
  onsite_count: number;
  paid_count: number;
  pending_payment_count: number;
  completed_count: number;
  total_tuition_volume: number;
}

export interface AdminEnrollmentsResponse {
  enrollments: AdminEnrollmentItem[];
  kpis: AdminEnrollmentKpis;
}

export interface AdminEnrollmentFilterParams {
  search?: string;
  delivery_mode?: "all" | "online" | "onsite" | string;
  curriculum_type?: "all" | "course" | "track" | string;
  payment_status?: "all" | "paid" | "unpaid" | string;
  status?: "all" | "active" | "completed" | "pending" | string;
  signal?: AbortSignal;
}

export interface ManualEnrollmentPayload {
  delivery_mode: "online" | "onsite";
  student_id?: number;
  full_name?: string;
  email?: string;
  phone_number?: string;
  address?: string;
  curriculum_type: "course" | "track";
  curriculum_id: number;
  is_paid?: boolean;
  is_enrolled?: boolean;
  payment_amount?: number;
  payment_method?: string;
}

export interface UpdateEnrollmentPayload {
  is_enrolled?: boolean;
  is_paid?: boolean;
  is_completed?: boolean;
  payment_amount?: number;
  payment_method?: string;
  progress_percent?: number;
  full_name?: string;
  email?: string;
  phone_number?: string;
  address?: string;
}

const getClient = () => {
  try {
    return authClientWeb();
  } catch {
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL || "",
    });
  }
};

export const enrollmentService = {
  // Fetch unified list of enrollments with KPI metrics
  async getAdminEnrollments(params?: AdminEnrollmentFilterParams): Promise<AdminEnrollmentsResponse> {
    const client = getClient();
    const queryParams: Record<string, any> = {};

    if (params?.search) queryParams.search = params.search;
    if (params?.delivery_mode && params.delivery_mode !== "all") queryParams.delivery_mode = params.delivery_mode;
    if (params?.curriculum_type && params.curriculum_type !== "all") queryParams.curriculum_type = params.curriculum_type;
    if (params?.payment_status && params.payment_status !== "all") queryParams.payment_status = params.payment_status;
    if (params?.status && params.status !== "all") queryParams.status = params.status;

    const res = await client.get("/course/admin-enrollments/", {
      params: queryParams,
      signal: params?.signal,
    });
    return res.data;
  },

  // Fetch individual enrollment detail
  async getEnrollmentDetail(id: number | string, mode: "online" | "onsite", signal?: AbortSignal): Promise<AdminEnrollmentItem> {
    const client = getClient();
    const res = await client.get(`/course/admin-enrollments/${mode}/${id}/`, { signal });
    return res.data;
  },

  // Update enrollment status, payment, or progress
  async updateEnrollment(
    id: number | string,
    mode: "online" | "onsite",
    data: UpdateEnrollmentPayload
  ): Promise<AdminEnrollmentItem> {
    const client = getClient();
    const res = await client.patch(`/course/admin-enrollments/${mode}/${id}/`, data);
    return res.data;
  },

  // Manual student enrollment by SuperAdmin
  async manualEnroll(data: ManualEnrollmentPayload): Promise<{ message: string; enrollment: AdminEnrollmentItem }> {
    const client = getClient();
    const res = await client.post("/course/admin-enrollments/manual/", data);
    return res.data;
  },

  // Cancel or delete an enrollment record
  async deleteEnrollment(id: number | string, mode: "online" | "onsite"): Promise<{ message: string }> {
    const client = getClient();
    const res = await client.delete(`/course/admin-enrollments/${mode}/${id}/`);
    return res.data;
  },
};
